import type { ReactNode } from 'react'
import { Tooltip } from '@diwata/ironvale'
import { GLYPHS, glossFor, type GlyphDef } from '@/content/vocabulary'
import './help-hint.css'

// HelpHint — the toolkit's one hover-help affordance. It WRAPS ironvale's Tooltip
// primitive; it never re-implements hover/focus/aria. All accessibility (opens on
// hover + :focus-visible, dismiss on Escape/blur, role="tooltip", aria-describedby,
// reduced motion) is inherited from Tooltip. HelpHint's only job is to resolve a
// gloss and hand Tooltip a valid, focusable trigger element.

export interface HelpHintProps {
  /** Registry key → VOCAB[term].gloss. */
  term?: string
  /** Literal gloss; overrides/bypasses the registry (entity-type words, glyph
   *  statuses, column codes — anything not a frozen platform term). */
  gloss?: string
  /** Decorate mode: wrap this text as the trigger. Omit → standalone "?" mark. */
  children?: ReactNode
  /** Pass-through to Tooltip. */
  placement?: 'top' | 'bottom'
  className?: string
  id?: string
}

/**
 * Term → gloss resolution is a single rule: a literal `gloss` wins, else the
 * registry lookup. A falsy result degrades gracefully — never an empty bubble,
 * never a throw.
 */
export function HelpHint({ term, gloss, children, placement = 'top', className, id }: HelpHintProps) {
  const label = gloss ?? glossFor(term)

  if (!label) {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`HelpHint: no gloss for term "${term ?? ''}"`)
    }
    // Degrade to plain text (or nothing) — a missing gloss must never break a page.
    return <>{children ?? null}</>
  }

  const trigger =
    children != null ? (
      // DECORATE — subtle dotted underline + cursor:help; tabIndex makes it
      // focusable so keyboard users get the bubble. No role: it is an annotation.
      <span className="help-hint help-hint--term" tabIndex={0}>
        {children}
      </span>
    ) : (
      // STANDALONE — a small superscript "?" glyph. A real <button> guarantees
      // native focus + Tooltip's focus wiring.
      <button type="button" className="help-hint__mark" aria-label={`What is ${term ?? 'this'}?`}>
        ?
      </button>
    )

  return (
    <Tooltip label={label} placement={placement} id={id} className={className}>
      {trigger}
    </Tooltip>
  )
}

// ── Legend ────────────────────────────────────────────────────────────────
// A compact key for a glyph SET (spring status, mission status, familiar kinds),
// read from GLYPHS so the copy lives once. Each row is itself hover-help: the
// glyph carries its status phrase, and glyphs tied to a MANUAL concept (GlyphDef.term)
// deepen to that term's gloss.

export interface LegendProps {
  family: keyof typeof GLYPHS | string
  className?: string
}

export function Legend({ family, className }: LegendProps) {
  const defs: GlyphDef[] = GLYPHS[family] ?? []
  if (defs.length === 0) return null
  return (
    <div className={['help-legend', className].filter(Boolean).join(' ')} role="list">
      {defs.map((d) => (
        <span className="help-legend__item" role="listitem" key={d.glyph + d.label}>
          <HelpHint gloss={d.label}>
            <span className="help-legend__glyph" aria-hidden="true">
              {d.glyph}
            </span>
          </HelpHint>
          <span className="help-legend__label">{d.label}</span>
        </span>
      ))}
    </div>
  )
}
